import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useDeal as useDealContext } from '@/contexts/deal-context';
import type { VerificationItemsUpdate } from '@/contexts/deal-context';
import { DEALS_QUERY_KEY, ensureRequiredChecks, enforceVerificationOrder, useUpdateDeal } from '@/hooks/use-deals';
import type { Deal } from '@/hooks/use-deals';
import type { VerificationItem } from './types';
import { verificationFingerprint } from './fingerprint';

/**
 * Adds to the deal's verification document from outside the editor (e.g. the
 * contacts panel). If the document is open, the change goes into the editor
 * and is autosaved from there. Otherwise it is saved directly from a freshly
 * fetched copy, with the same stale-copy guard the editor uses.
 */
export function useAddToVerificationDocument() {
  const { dealId, applyToOpenVerificationDocument } = useDealContext();
  const queryClient = useQueryClient();
  const { mutateAsync: updateDeal } = useUpdateDeal();

  return useCallback(async (update: VerificationItemsUpdate): Promise<void> => {
    if (!dealId)
      throw new Error('No deal selected');
    if (applyToOpenVerificationDocument(update))
      return;

    const queryKey = [...DEALS_QUERY_KEY, dealId];
    await queryClient.refetchQueries({ queryKey, exact: true });
    const latest = queryClient.getQueryData<Deal>(queryKey);
    const body = latest?.verification_document?.body;
    if (!latest || !body)
      throw new Error('Verification document not found');

    const items = JSON.parse(body) as VerificationItem[];
    await updateDeal({
      id: dealId,
      verification_document: {
        body: JSON.stringify(enforceVerificationOrder(latest, ensureRequiredChecks(latest, update(items)))),
      },
      expected_verification_hash: await verificationFingerprint(body),
    });
  }, [applyToOpenVerificationDocument, dealId, queryClient, updateDeal]);
}
